import { useMemo, useState } from 'react'

import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'

import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import PushPinIcon from '@material-ui/icons/Room'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'

import { MaxConversationSummary } from '../../types'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    width: 'calc(100% - 1rem)',
    margin: '0 auto',
    borderRadius: 10,
    border: '1px solid transparent',
    borderColor: 'var(--item-border-color)',
    transform: 'translateX(0)',
    backgroundColor: 'transparent',
    transition: 'background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease',
    '--menu-bg': 'var(--ds-bg-surface, #ffffff)',
    '--menu-fade-bg': 'var(--ds-bg-surface, #ffffff)',
    '--item-border-color': 'transparent',
    '&:hover': {
      transform: 'translateX(0.25rem)',
      backgroundColor: 'var(--ds-bg-surface-hover, rgba(241, 242, 247, 0.45))',
      '--item-border-color': 'var(--ds-border-default, #d7dae4)',
      '--menu-bg': 'var(--ds-bg-surface-hover, rgba(241, 242, 247, 0.45))',
      '--menu-fade-bg':
        'color-mix(in oklch, var(--ds-bg-surface, #ffffff) 60%, var(--ds-bg-subtle, #f1f2f7) 40%)',
    },
    '&:hover $menuButton, &$menuOpen $menuButton': {
      opacity: 1,
      pointerEvents: 'auto',
      backgroundColor: 'var(--menu-fade-bg)',
    },
    '&:hover $surfaceFade, &$menuOpen $surfaceFade': {
      opacity: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      border: '1px solid var(--item-border-color)',
      pointerEvents: 'none',
      zIndex: 4,
    },
    '&:focus-within': {
      borderColor: 'var(--ds-border-focus, #f47d6b)',
      boxShadow: '0 0 0 2px rgba(244, 125, 107, 0.24)',
    },
  },
  menuOpen: {
    transform: 'translateX(0.25rem)',
    backgroundColor: 'var(--ds-bg-surface-hover, rgba(241, 242, 247, 0.45))',
    '--item-border-color': 'var(--ds-border-default, #d7dae4)',
    '--menu-bg': 'var(--ds-bg-surface-hover, rgba(241, 242, 247, 0.45))',
    '--menu-fade-bg':
      'color-mix(in oklch, var(--ds-bg-surface, #ffffff) 60%, var(--ds-bg-subtle, #f1f2f7) 40%)',
  },
  active: {
    transform: 'none !important' as const,
    backgroundColor: 'var(--color-coral-50, #fff4f2)',
    '--item-border-color': 'transparent',
    '--menu-bg': 'var(--color-coral-50, #fff4f2)',
    '--menu-fade-bg': 'var(--color-coral-50, #fff4f2)',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      borderRadius: '10px 0 0 10px',
      background: 'var(--color-coral-500, #f47d6b)',
      zIndex: 1,
    },
  },
  surface: {
    position: 'relative',
    borderRadius: 'inherit',
    overflow: 'hidden',
  },
  surfaceFade: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '6rem',
    background:
      'linear-gradient(90deg, transparent 0%, var(--menu-fade-bg) 55%, var(--menu-fade-bg) 100%)',
    opacity: 0,
    pointerEvents: 'none',
    zIndex: 2,
    transition: 'opacity 200ms ease',
    borderRadius: 'inherit',
  },
  menuButtonVisible: {
    opacity: 1,
    pointerEvents: 'auto',
    backgroundColor: 'var(--menu-fade-bg)',
  },
  mainButton: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    justifyContent: 'flex-start',
    textTransform: 'none',
    borderRadius: 'inherit',
    border: 0,
    backgroundColor: 'transparent',
    padding: theme.spacing(1, 5, 1, 1.5),
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      boxShadow: 'inset 0 0 0 1px var(--ds-border-focus, #f47d6b)',
      outline: 'none',
    },
  },
  meta: {
    textAlign: 'left',
    width: '100%',
    position: 'relative',
    zIndex: 10,
    paddingRight: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
  },
  title: {
    fontWeight: 500,
    fontSize: '0.8125rem',
    lineHeight: 1.3,
    color: 'var(--ds-fg-primary, #1f2430)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  time: {
    marginTop: 2,
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    fontSize: '0.6875rem',
  },
  menuButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: 0,
    bottom: 0,
    width: 28,
    borderRadius: theme.spacing(0.75),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    pointerEvents: 'none',
    color: 'var(--ds-fg-secondary, #647089)',
    transition: 'opacity 200ms ease, color 200ms ease, background-color 200ms ease',
    zIndex: 3,
    '&:hover': {
      color: 'var(--ds-fg-primary, #1f2430)',
      backgroundColor: 'var(--menu-fade-bg)',
    },
    '&.Mui-focusVisible': {
      opacity: 1,
      pointerEvents: 'auto',
      color: 'var(--ds-fg-primary, #1f2430)',
      backgroundColor: 'var(--menu-fade-bg)',
      boxShadow: '0 0 0 2px rgba(244, 125, 107, 0.24)',
      outline: 'none',
    },
  },
  editRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1),
    position: 'relative',
    zIndex: 5,
  },
  editField: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1),
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--ds-border-default, #d7dae4)',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--color-coral-500, #f47d6b)',
      },
      '&.Mui-focused': {
        boxShadow: '0 0 0 2px rgba(244, 125, 107, 0.24)',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: 'var(--ds-fg-primary, #1f2430)',
      fontSize: '1rem',
      padding: theme.spacing(1, 1.5),
    },
  },
  editActionButton: {
    width: 32,
    height: 32,
    borderRadius: theme.spacing(1),
    border: '1px solid var(--ds-border-default, #d7dae4)',
    color: 'var(--ds-fg-secondary, #647089)',
    backgroundColor: 'var(--ds-bg-surface, #ffffff)',
    '&:hover': {
      backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
      color: 'var(--ds-fg-primary, #1f2430)',
    },
    '&.Mui-focusVisible': {
      boxShadow: '0 0 0 2px rgba(244, 125, 107, 0.24)',
      outline: 'none',
    },
  },
  menuPaper: {
    borderRadius: theme.spacing(1.5),
    border: '1px solid var(--ds-border-default, #d7dae4)',
    backgroundColor: 'var(--ds-bg-surface, #ffffff)',
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
  },
  menuItem: {
    fontSize: '0.85rem',
    color: 'var(--ds-fg-primary, #1f2430)',
    '&:hover': {
      backgroundColor: 'var(--ds-bg-subtle, #f1f2f7)',
    },
    '& .MuiListItemIcon-root': {
      minWidth: 28,
      color: 'inherit',
    },
    '& .MuiListItemText-primary': {
      fontSize: '0.85rem',
    },
  },
  menuDelete: {
    color: 'var(--ds-accent-error, #ef4444)',
  },
}))

const renderRelativeTime = (timestamp: number): string => {
  const diffMs = Date.now() - timestamp
  const diffMinutes = Math.floor(diffMs / (60 * 1000))

  if (diffMinutes < 1) return 'Now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`

  return new Date(timestamp).toLocaleDateString()
}

type ConversationItemProps = {
  conversation: MaxConversationSummary
  isActive: boolean
  onSelectConversation: (conversationId: string) => void
  onRenameConversation: (conversation: MaxConversationSummary, nextTitle: string) => Promise<void> | void
  onTogglePinConversation: (conversation: MaxConversationSummary) => Promise<void> | void
  onDeleteConversation: (conversation: MaxConversationSummary) => Promise<void> | void
}

const ConversationItem = ({
  conversation,
  isActive,
  onSelectConversation,
  onRenameConversation,
  onTogglePinConversation,
  onDeleteConversation,
}: ConversationItemProps) => {
  const classes = useStyles()

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(conversation.title)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isPinning, setIsPinning] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const isMenuOpen = Boolean(menuAnchorEl)
  const formattedTime = useMemo(() => renderRelativeTime(conversation.updatedAt), [conversation.updatedAt])

  const closeMenu = () => {
    setMenuAnchorEl(null)
  }

  const handleSelect = () => {
    if (isEditing) return
    onSelectConversation(conversation.id)
  }

  const handleStartRename = () => {
    closeMenu()
    setEditValue(conversation.title)
    setIsEditing(true)
  }

  const handleCancelRename = () => {
    setIsEditing(false)
    setEditValue(conversation.title)
    setIsRenaming(false)
  }

  const handleConfirmRename = async () => {
    const trimmed = editValue.trim()
    if (!trimmed || trimmed === conversation.title) {
      handleCancelRename()
      return
    }

    setIsRenaming(true)
    try {
      await onRenameConversation(conversation, trimmed)
      setIsEditing(false)
    } finally {
      setIsRenaming(false)
    }
  }

  const handleTogglePin = async () => {
    closeMenu()
    setIsPinning(true)
    try {
      await onTogglePinConversation(conversation)
    } finally {
      setIsPinning(false)
    }
  }

  const handleDelete = async () => {
    closeMenu()
    setIsDeleting(true)
    try {
      await onDeleteConversation(conversation)
    } finally {
      setIsDeleting(false)
    }
  }

  if (isEditing) {
    return (
      <div className={classes.root}>
        <div className={classes.surface}>
          <div className={classes.editRow}>
            <TextField
              className={classes.editField}
              size="small"
              variant="outlined"
              value={editValue}
              disabled={isRenaming}
              onChange={(event) => setEditValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void handleConfirmRename()
                }
                if (event.key === 'Escape') {
                  event.preventDefault()
                  handleCancelRename()
                }
              }}
              autoFocus
            />
            <IconButton
              size="small"
              aria-label="Confirm rename"
              className={classes.editActionButton}
              disableRipple
              onClick={() => void handleConfirmRename()}
              disabled={isRenaming}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Cancel rename"
              className={classes.editActionButton}
              disableRipple
              onClick={handleCancelRename}
              disabled={isRenaming}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <span className={classes.surfaceFade} aria-hidden="true" />
        </div>
      </div>
    )
  }

  const rootClassName = [classes.root, isActive ? classes.active : '', isMenuOpen ? classes.menuOpen : '']
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClassName}>
      <div className={classes.surface}>
        <Button
          className={classes.mainButton}
          variant="text"
          disableRipple
          onClick={handleSelect}
          disabled={isPinning || isDeleting}
        >
          <div className={classes.meta}>
            <Typography variant="body2" className={classes.title}>
              {conversation.title || 'Untitled Conversation'}
            </Typography>
            <Typography variant="caption" className={classes.time}>
              {formattedTime}
            </Typography>
          </div>
        </Button>

        {!conversation.isTemporary && (
          <IconButton
            size="small"
            className={`${classes.menuButton} ${isMenuOpen ? classes.menuButtonVisible : ''}`}
            aria-label="Open conversation menu"
            disableRipple
            onClick={(event) => setMenuAnchorEl(event.currentTarget)}
            disabled={isPinning || isDeleting}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        )}

        <span className={classes.surfaceFade} aria-hidden="true" />
      </div>

      {!conversation.isTemporary && (
        <Menu
          anchorEl={menuAnchorEl}
          keepMounted
          open={isMenuOpen}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          classes={{ paper: classes.menuPaper }}
        >
          <MenuItem onClick={() => void handleTogglePin()} className={classes.menuItem}>
            <ListItemIcon>
              <PushPinIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={conversation.isPinned ? 'Unpin' : 'Pin'} />
          </MenuItem>
          <MenuItem onClick={handleStartRename} className={classes.menuItem}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Rename" />
          </MenuItem>
          <MenuItem onClick={() => void handleDelete()} className={`${classes.menuItem} ${classes.menuDelete}`}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" className={classes.menuDelete} />
            </ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        </Menu>
      )}
    </div>
  )
}

export default ConversationItem
